import React, { useEffect, useState } from 'react';
import {
	Box,
	FormControl,
	FormHelperText,
	FormLabel,
	Skeleton,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import { useUpdateDealRowMutation } from '@/features/deals/dealsApi';
import { useUpdateClientRowMutation } from '@/features/clients/clientsApi';
import { LOSS_REASON } from '@/components/forms/CreateNewBusinessForm/validationSchema';
import { useObjectContext } from '@/hooks/useObjectContext';
import { minimalInputTheme, minimalLabelTheme } from '@/app/theme';

const MAX_LENGTH = 300;

export const LossReason = ({ minimal = true }) => {
	const dispatch = useDispatch();
	const { dealId, dealRow, clientId, clientRow, isClientView: isClient } = useObjectContext();

	const isProspect = isClient && clientRow?.isProspect;

	const currentValue = (isProspect ? clientRow?.[LOSS_REASON] : dealRow?.[LOSS_REASON]) ?? '';
	const [lossReason, setLossReason] = useState(currentValue);

	useEffect(() => {
		setLossReason(currentValue);
	}, [currentValue]);

	const [updateDealRow, { isLoading: isUpdatingDealRow }] = useUpdateDealRowMutation();
	const [updateClientRow, { isLoading: isUpdatingClient }] = useUpdateClientRowMutation();
	const isLoading = isUpdatingDealRow || isUpdatingClient;

	const updateLossReason = async () => {
		const nextValue = lossReason.trim();
		if (nextValue === (currentValue ?? '').trim()) return;

		try {
			if (isProspect) {
				await updateClientRow({
					clientId,
					docId: clientRow?.docId,
					properties: {
						[LOSS_REASON]: nextValue,
					},
				}).unwrap();
				return;
			}

			await updateDealRow({
				dealId: dealRow?.dealId ?? dealId,
				docId: dealRow?.id,
				properties: {
					[LOSS_REASON]: nextValue,
				},
			}).unwrap();
		} catch (err) {
			console.error('Failed to update loss reason:', err);
			dispatch(
				showToast({
					message: 'Failed to update loss reason. Please try again.',
					error: true,
				}),
			);
		}
	};

	if (!(isProspect ? clientRow : dealRow)) {
		return <Skeleton animation='wave' width='55%' height='2em' />;
	}

	return (
		<FormControl fullWidth>
			<FormLabel>Loss Reason</FormLabel>
			<Stack>
				<TextField
					fullWidth
					disabled={isLoading}
					multiline
					rows={3}
					variant={minimal ? 'standard' : 'outlined'}
					sx={minimal ? minimalInputTheme : {}}
					className={minimal ? 'minimal-input' : null}
					value={lossReason}
					onChange={(event) => setLossReason(event.target.value)}
					onBlur={updateLossReason}
					inputProps={{ maxLength: MAX_LENGTH }}
				/>

				<FormHelperText
					sx={{
						textAlign: 'right',
						...(lossReason.length === MAX_LENGTH && { fontWeight: 'bold' }),
					}}
				>
					{`${lossReason.length}/${MAX_LENGTH}`}
				</FormHelperText>
			</Stack>
		</FormControl>
	);
};
